/**
 * Hero Carousel Component
 * Displays featured products in an auto-rotating carousel with navigation
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ProductSummary } from '../../services/staticContent';
import { getProductImageUrl } from '../../utils/imageUtils';
import { useCurrency } from '../../contexts/CurrencyContext';
import {
  ChevronLeft,
  ChevronRight,
  Star,
  ShoppingBag,
  ArrowRight,
  Shield,
  Zap,
  CreditCard,
} from 'lucide-react';

interface HeroCarouselProps {
  products: ProductSummary[];
  autoPlayInterval?: number;
}

export function HeroCarousel({ products, autoPlayInterval = 5000 }: HeroCarouselProps): JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { formatPrice } = useCurrency();

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const goToNext = useCallback(() => {
    const nextIndex = (currentIndex + 1) % products.length;
    goToSlide(nextIndex);
  }, [currentIndex, products.length, goToSlide]);

  const goToPrev = useCallback(() => {
    const prevIndex = (currentIndex - 1 + products.length) % products.length;
    goToSlide(prevIndex);
  }, [currentIndex, products.length, goToSlide]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || products.length <= 1) return;

    const interval = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [isAutoPlaying, autoPlayInterval, goToNext, products.length]);

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  if (products.length === 0) {
    return <HeroFallback />;
  }

  const currentProduct = products[currentIndex];

  return (
    <section
      className="relative min-h-[600px] md:min-h-[650px] overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
          style={{
            backgroundImage: `url(${getProductImageUrl(currentProduct.imageUrl)})`,
            transform: isTransitioning ? 'scale(1.05)' : 'scale(1)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-gray-900/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 container-custom h-full min-h-[600px] md:min-h-[650px] flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-16">
          {/* Left: Product Info */}
          <div
            className={`text-white transition-all duration-500 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm font-semibold rounded-full">
                <Star size={14} fill="currentColor" />
                Featured
              </span>
              {currentProduct.qualityTier === 'premium' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-sm font-semibold rounded-full">
                  <Star size={14} fill="currentColor" />
                  Premium
                </span>
              )}
              <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full capitalize">
                {currentProduct.category.replace('-', ' ')}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {currentProduct.title}
            </h1>

            {/* Description */}
            <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed max-w-xl">
              {currentProduct.shortDescription}
            </p>

            {/* Price & CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-10">
              <div className="text-4xl md:text-5xl font-bold text-white">
                {formatPrice(currentProduct.price.amount, currentProduct.price.currency)}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/products/${currentProduct.id}`}
                  className="btn bg-primary-600 hover:bg-primary-700 text-white inline-flex items-center justify-center gap-2 text-lg px-8 py-3"
                >
                  <ShoppingBag size={20} />
                  View Product
                </Link>
                <Link
                  to="/products"
                  className="btn bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white hover:text-gray-900 text-white inline-flex items-center justify-center gap-2 text-lg px-8 py-3 transition-all"
                >
                  Browse All
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-white/20">
              <div className="flex items-center gap-2 text-white/80">
                <Shield size={18} />
                <span className="text-sm">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Zap size={18} />
                <span className="text-sm">Instant Download</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <CreditCard size={18} />
                <span className="text-sm">One-Time Purchase</span>
              </div>
            </div>
          </div>

          {/* Right: Product Image Card (visible on lg+) */}
          <div className="hidden lg:block">
            <div
              className={`transition-all duration-500 ${
                isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
              }`}
            >
              <Link
                to={`/products/${currentProduct.id}`}
                className="block relative group"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform group-hover:scale-[1.02] transition-transform duration-300">
                  <img
                    src={getProductImageUrl(currentProduct.imageUrl)}
                    alt={currentProduct.title}
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex items-center justify-between">
                      <span className="text-white/80 text-sm">Click to view details</span>
                      <ArrowRight size={20} className="text-white group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {products.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {/* Pagination Dots */}
      {products.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 ${
                index === currentIndex
                  ? 'w-10 h-3 bg-primary-500 rounded-full'
                  : 'w-3 h-3 bg-white/40 hover:bg-white/60 rounded-full'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Progress Bar */}
      {products.length > 1 && isAutoPlaying && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
          <div
            className="h-full bg-primary-500 transition-all"
            style={{
              animation: `progress ${autoPlayInterval}ms linear`,
              width: '100%',
            }}
            key={currentIndex}
          />
        </div>
      )}

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}

/**
 * Fallback hero when no featured products available
 */
function HeroFallback(): JSX.Element {
  return (
    <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white py-20 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full border-4 border-white" />
        <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full border-4 border-white" />
      </div>

      <div className="container-custom relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Master Gambling Strategies
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90 leading-relaxed">
            Educational guides and systems for horse racing, card games, roulette, and more.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/products"
              className="btn bg-white text-primary-800 hover:bg-gray-100 inline-flex items-center justify-center gap-2 text-lg px-8 py-3"
            >
              <ShoppingBag size={20} />
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroCarousel;
